/**
 * The api's clock, which "today", hold countdowns and scan times follow instead of the
 * device's: a phone's clock may be wrong, and a rehearsal may run the api on a demo
 * clock set to the storyline's day (DEMO_CLOCK). Each app asks the api its time as it
 * starts (syncServerClock) and reads serverNow() wherever it would read Date.now().
 */

type StorageLike = { getItem(key: string): string | null; setItem(key: string, value: string): void };

// Kept so a conductor's scan reopened with no signal stays on the api's day.
const storageKey = "yatayat.serverClockOffsetMs";

function storage(): StorageLike | undefined {
  try {
    return (globalThis as { localStorage?: StorageLike }).localStorage;
  } catch {
    return undefined;
  }
}

function storedOffset(): number {
  try {
    const v = Number(storage()?.getItem(storageKey) ?? 0);
    return Number.isFinite(v) ? v : 0;
  } catch {
    return 0;
  }
}

let offsetMs = storedOffset();

/** The api's time now, in milliseconds since the epoch. */
export function serverNow(): number {
  return serverTimeAt(Date.now());
}

/** The api's time at an instant the device's clock read as deviceMs, such as a query's dataUpdatedAt. */
export function serverTimeAt(deviceMs: number): number {
  return deviceMs + offsetMs;
}

/**
 * Follows the api's clock from now on, given its time from a response that left at
 * sentAtMs and arrived at receivedAtMs on the device's clock: the api read it halfway.
 */
export function setServerTime(serverMs: number, sentAtMs: number, receivedAtMs: number): void {
  offsetMs = Math.round(serverMs - (sentAtMs + receivedAtMs) / 2);
  try {
    storage()?.setItem(storageKey, String(offsetMs));
  } catch {
    // Storage off or full: the offset lasts until the page closes.
  }
}

/**
 * Asks the api its time with askTime, which resolves to GET /api/v0/time's `now`, and
 * follows it. When askTime fails the last offset this device saw stays. Never rejects.
 */
export async function syncServerClock(askTime: () => Promise<string>): Promise<void> {
  const sentAt = Date.now();
  try {
    const at = Date.parse(await askTime());
    if (Number.isFinite(at)) setServerTime(at, sentAt, Date.now());
  } catch {
    // Offline, slow or no api.
  }
}
